/**
 * Foreground routing for session-event pushes.
 *
 * Android session pushes are data-only. The native renderer posts a tray
 * notification only when the app is backgrounded/killed; when the app is
 * FOREGROUNDED it skips the tray and still forwards the Capacitor
 * `pushNotificationReceived` event. This module decides whether that event
 * should become an in-app banner — never a second tray toast.
 *
 * iOS session pushes already present as APNs alerts while foregrounded
 * (`capacitor.config.ts` `presentationOptions` includes `'alert'`), so this
 * resolver returns `null` on iOS to avoid a double banner.
 *
 * Call / announcement handling stays in `push-registration.ts` and is
 * untouched: those types return `null` here.
 */

import {
  isSessionReminder,
  resolveSessionReminderRoute,
  type SessionReminderData,
} from '../session-reminder-handler'

export type ForegroundPushNotification = {
  title?: string
  body?: string
  data?: SessionReminderData & Record<string, string>
}

export type ForegroundSessionBanner = {
  title: string
  body: string
  route: string
}

/** Matches `PushPlatform` in `push-registration.ts` (`ANDROID` | `IOS`). */
export type ForegroundSessionPushPlatform = 'ANDROID' | 'IOS'

/** True when the WebView is visible — the JS stand-in for native `appInForeground`. */
export function isDocumentForegrounded(
  doc: Pick<Document, 'hidden' | 'visibilityState'> | null | undefined = typeof document ===
  'undefined'
    ? undefined
    : document,
): boolean {
  if (!doc) return false
  return doc.visibilityState === 'visible' && !doc.hidden
}

/**
 * Session types + foreground → banner (title/body from the push, route to the
 * session). Call/announcement/unknown types, a backgrounded app (native
 * already posted the tray), or iOS (APNs already presents an alert) → `null`.
 */
export function resolveForegroundSessionBanner(
  notification: ForegroundPushNotification,
  options: { appForegrounded: boolean; platform: ForegroundSessionPushPlatform },
): ForegroundSessionBanner | null {
  if (options.platform !== 'ANDROID') return null
  if (!options.appForegrounded) return null
  const data = notification.data ?? {}
  if (!isSessionReminder(data)) return null
  const route = resolveSessionReminderRoute(data)
  if (!route) return null
  const title = (notification.title ?? data.title ?? '').trim()
  const body = (notification.body ?? data.body ?? '').trim()
  if (!title && !body) return null
  return {
    title: title || 'Session',
    body,
    route,
  }
}
